import { useCallback, useRef } from 'react';
import { updatePaginationValues } from '~/store/dailyparts/actions';

export const usePagination = ({
  pagination,
  dispatch,
  searchAction,
  filters,
  listType,
}) => {
  const sortState = useRef({ orderBy: -1, orderDirection: 'asc' });

  const onChangePage = useCallback(
    (page, tableRef) => {
      if (!tableRef) {
        return;
      }

      const { dataManager } = tableRef;
      const tableRefState = dataManager.getRenderState();
      const { pageSize } = tableRefState;

      const {
        batchSize = 500,
        batchPages = [],
        loadingMore = false,
      } = pagination || {};

      const lastBatchLoaded = batchPages?.at(-1);
      const loadedDataLength = page * pageSize;
      const canLoadMore = loadedDataLength >= (lastBatchLoaded + 1) * batchSize - pageSize * 2;

      if (canLoadMore && pagination?.total > loadedDataLength && !loadingMore) {
        const nextBatchPage = lastBatchLoaded + 1;
        dispatch(updatePaginationValues({ listType, loadingMore: true }));

        const searchParams = {
          parameters: filters,
          page,
          pageSize,
          nextBatchPage,
          limit: batchSize,
          reorder: false,
        };
        dispatch(searchAction(searchParams));
      } else {
        dataManager.changeCurrentPage(page);
        const updateParams = { listType, page, pageSize };
        dispatch(updatePaginationValues(updateParams));
      }
    },
    [dispatch, searchAction, pagination, filters],
  );

  const onSearchChange = useCallback((tableRef) => {
    if (!tableRef) return;
    const { dataManager } = tableRef;
    const { pageSize } = dataManager.getRenderState();
    dataManager.changeCurrentPage(0);

    const isEditingRows = dataManager?.searchedData?.every(
      item => item?.tableData?.editing === 'update',
    ) || false;

    if (!isEditingRows) {
      dispatch(updatePaginationValues({ listType, page: 0, pageSize }));
    }
  });

  const onOrderChange = useCallback(
    ({ tableRef, orderBy, orderDirection }) => {
      if (!tableRef) return;
      const { dataManager } = tableRef;

      if (dataManager) {
        const { pageSize } = dataManager.getRenderState();
        dataManager.changeCurrentPage(0);
        dispatch(updatePaginationValues({ listType, page: 0, pageSize }));
      }

      sortState.current = { orderBy, orderDirection };
    },
  );

  const applyDefaultSort = useCallback((columns) => {
    if (sortState.current.orderBy === -1) {
      return columns;
    }

    const mappedColumns = columns.map((col, index) => {
      if (index !== sortState.current.orderBy) return col;
      return { ...col, defaultSort: sortState.current.orderDirection };
    });

    return mappedColumns;
  }, []);

  return {
    onChangePage, onSearchChange, onOrderChange, applyDefaultSort,
  };
};
